// PROA ECON-ATTN-01A. Detección de precio: cribado, no ficha bilateral ni
// notificación confirmada. Histórico de la misma relación cliente-producto.
import { Fragment, useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { backLabel } from '../../pricingJourney/navigation';
import {
  Alert, Box, Button, Chip, Paper, Stack,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Typography,
} from '@mui/material';
import CustomerRelationshipWorkspace from '../components/CustomerRelationshipWorkspace';
import MarginCenterSpecializedPageHeader from '../components/MarginCenterSpecializedPageHeader';
import { getEconomicPriceObservations } from '../services/economicPriceObservationService';
import type { EconomicPriceObservation } from '../types/economicPriceObservations';


import { pageFrameSx } from '../../pricingJourney/components/PageHeader';
import { SkeletonTable } from '../../pricingJourney/components/Skeletons';
import { toSpanishNumber } from '../../../modules/pricingJourney/formatNumber';

const fmt = (value: number | null | undefined, digits = 2) => value == null
  ? '—' : toSpanishNumber(value, { maximumFractionDigits: digits });
const date = (value: string | null | undefined) => value
  ? value.slice(0, 10).split('-').reverse().join('/') : '—';
const safeReturn = (value: string | null) =>
  value?.startsWith('/margin-center/') && !value.startsWith('//')
    ? value : '/margin-center/control-comercial';

function Evidence({ item, investigate }: {
  item: EconomicPriceObservation;
  investigate: () => void;
}) {
  const bars = [
    { id: 'b2', name: 'Histórico 1', price: item.observed_revenue.b2 / item.observed_units.b2,
      days: item.purchase_days.b2, start: item.window_dates.b2_start,
      end: item.window_dates.b2_end, tone: '#64748b' },
    { id: 'b1', name: 'Histórico 2', price: item.observed_revenue.b1 / item.observed_units.b1,
      days: item.purchase_days.b1, start: item.window_dates.b1_start,
      end: item.window_dates.b1_end, tone: '#94a3b8' },
    { id: 'w1', name: 'Periodo anterior', price: item.previous_price,
      days: item.purchase_days.w1, start: item.window_dates.w1_start,
      end: item.window_dates.w1_end, tone: '#38bdf8' },
    { id: 'w0', name: 'Periodo reciente', price: item.current_price,
      days: item.purchase_days.w0, start: item.window_dates.w0_start,
      end: item.window_dates.w0_end, tone: '#fbbf24' },
  ];
  const maxPrice = Math.max(1, ...bars.map((bar) => bar.price));
  return (
    <Box sx={{ p: 1.35, bgcolor: '#0b1426' }}>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 1, mb: 1.25 }}>
        {[
          ['Precio neto de referencia', `${fmt(item.reference_price, 4)} ${item.currency_code}/ud.`],
          ['Precio neto reciente', `${fmt(item.current_price, 4)} ${item.currency_code}/ud.`],
          ['Diferencia observada', `${fmt(item.price_delta_pct)} %`],
        ].map(([label, value]) => (
          <Box key={label} sx={{ border: '1px solid #334155', borderRadius: 1.5, p: 1.1 }}>
            <Typography variant="caption" color="text.secondary">{label}</Typography>
            <Typography variant="h6" sx={{ fontWeight: 850 }}>{value}</Typography>
          </Box>
        ))}
      </Box>
      <Typography variant="caption" sx={{ display: 'block', color: '#7dd3fc', fontWeight: 800, mb: 0.8 }}>
        Evolución del precio neto unitario · misma relación cliente-producto
      </Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', xl: 'repeat(4, 1fr)' }, gap: 1 }}>
        {bars.map((bar) => (
          <Box key={bar.id} sx={{ border: '1px solid #334155', borderRadius: 1.5, p: 1 }}>
            <Typography variant="caption" sx={{ display: 'block', color: bar.tone, fontWeight: 800 }}>{bar.name}</Typography>
            <Typography variant="caption" color="text.secondary">{date(bar.start)} – {date(bar.end)}</Typography>
            <Box sx={{ height: 9, borderRadius: 1, bgcolor: '#1e293b', mt: 1.25, overflow: 'hidden' }}>
              <Box sx={{ height: '100%', width: `${(bar.price / maxPrice) * 100}%`, bgcolor: bar.tone }} />
            </Box>
            <Stack direction="row" justifyContent="space-between" sx={{ mt: 0.75 }}>
              <Typography variant="body2" sx={{ fontWeight: 800 }}>{fmt(bar.price, 4)} {item.currency_code}/ud.</Typography>
              <Typography variant="caption" color="text.secondary">{bar.days} días</Typography>
            </Stack>
          </Box>
        ))}
      </Box>
      <Stack direction={{ xs: 'column', md: 'row' }} gap={1} justifyContent="space-between" sx={{ mt: 1.2 }}>
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 750 }}>
            {item.change_vs_previous_pct < 0
              ? `Precio ${fmt(Math.abs(item.change_vs_previous_pct))} % menor que en el periodo anterior`
              : item.change_vs_previous_pct > 0
                ? `Precio ${fmt(item.change_vs_previous_pct)} % mayor que en el periodo anterior`
                : 'Precio sin variación frente al periodo anterior'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {item.horizon_days} días por ventana · {item.historical_intervals} intervalos históricos completos ·
            referencia prudente: {item.reference_source === 'Historico 1' ? 'Histórico 1' : 'Histórico 2'}.
            Variación observada; no demuestra pérdida económica, causalidad ni deterioro de margen.
          </Typography>
        </Box>
        <Button variant="outlined" size="small" onClick={investigate} sx={{ textTransform: 'none', flexShrink: 0 }}>
          Abrir relación cliente-producto
        </Button>
      </Stack>
    </Box>
  );
}

export default function MarginCenterEconomicPriceObservationsPage() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const cursor = params.get('cursor') || '';
  const [history, setHistory] = useState<string[]>([]);
  const [expandedKey, setExpandedKey] = useState<string | null>(null);
  const returnTo = safeReturn(params.get('returnTo'));
  const query = useQuery({
    queryKey: ['pricing', 'economic-price', 'observations', '1.2', cursor],
    queryFn: () => getEconomicPriceObservations({ limit: 25, cursor: cursor || undefined }),
    retry: 1, staleTime: 30000,
  });
  useEffect(() => { window.scrollTo({ top: 0, behavior: 'auto' }); }, [cursor]);
  const nextPage = () => {
    const nextCursor = query.data?.page.next_cursor;
    if (!nextCursor) return;
    setExpandedKey(null); setHistory((h) => [...h, cursor]);
    const next = new URLSearchParams(params); next.set('cursor', nextCursor); setParams(next);
  };
  const previousPage = () => {
    if (!history.length) return;
    const prior = history[history.length - 1];
    setExpandedKey(null); setHistory((h) => h.slice(0, -1));
    const next = new URLSearchParams(params);
    if (prior) next.set('cursor', prior); else next.delete('cursor');
    setParams(next);
  };
  const selectedCustomer = params.get('relationshipCustomer');
  const selectedProduct = params.get('relationshipProduct');
  const selectedRow = query.data?.items.find((i) => i.customer_id === selectedCustomer && i.product_id === selectedProduct);
  const investigate = (item: EconomicPriceObservation) => {
    const next = new URLSearchParams(params);
    next.set('relationshipCustomer', item.customer_id);
    next.set('relationshipProduct', item.product_id);
    setParams(next);
  };
  const closeRelationship = () => {
    const next = new URLSearchParams(params);
    next.delete('relationshipCustomer'); next.delete('relationshipProduct');
    setParams(next, { replace: true });
  };
  const comparison = params.get('comparison');
  const comparisonMode = comparison === 'none' || comparison === 'previous_period'
    || comparison === 'year_over_year' || comparison === 'custom'
    ? comparison : 'previous_period';
  const effectiveComparison = comparisonMode === 'custom'
    && !(params.get('comparisonFrom') && params.get('comparisonTo'))
    ? 'previous_period' : comparisonMode;
  const grain = params.get('grain');
  const timeGrain = grain === 'day' || grain === 'week' || grain === 'month' ? grain : 'week';
  const summary = query.data?.summary;
  const valid = query.data?.version === '1.2' && query.data.selection.mode === 'automatic_per_relationship';
  const cards = [
    ['Cambios de precio detectados', summary?.detected_count, 'Candidatas, no avisos confirmados', '#38bdf8'],
    ['Relaciones comparables', summary?.relationships_comparable, 'Con datos homogéneos y mínimo histórico', '#22c55e'],
    ['Con compras, no comparables', summary?.with_current_activity_not_comparable, 'No significa ausencia de problemas', '#fbbf24'],
  ] as const;
  return (
    <Box sx={pageFrameSx}>
      <Stack gap={1.1}>
        <MarginCenterSpecializedPageHeader
          backLabel={backLabel(returnTo)}
          onBack={() => navigate(returnTo)}
          eyebrow="Avisos comerciales"
          title="Condiciones económicas · cambios de precio"
          description="Relaciones con menor precio neto unitario observado. Cribado provisional, sin decisiones automáticas."
          meta={<Chip size="small" variant="outlined" label="Detección provisional" />}
        />
        {query.isPending && <SkeletonTable rows={6} />}
        {query.isError && <Alert severity="warning">La lectura económica no está disponible. No mostramos un cero ficticio.</Alert>}
        {query.data && !valid && <Alert severity="warning">Contrato económico incompatible. No mostramos resultados no verificados.</Alert>}
        {query.data && valid && <>
          <Paper elevation={0} sx={{ p: 1.3, bgcolor: '#0c172b', border: '1px solid #334155', borderRadius: 2 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1} sx={{ mb: 1 }}>
              <Typography variant="caption" sx={{ color: '#94a3b8' }}>Cartera cliente-producto · evaluación por histórico propio</Typography>
              <Chip label={`Datos hasta ${date(query.data.data_cutoff_date)}`} size="small" variant="outlined" />
            </Stack>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' }, gap: 1 }}>
              {cards.map(([title, count, help, tone]) => (
                <Box key={title} sx={{ p: 1, border: '1px solid #334155', borderRadius: 1.5, minWidth: 0 }}>
                  <Box sx={{ width: 32, height: 4, bgcolor: tone, borderRadius: 2 }} />
                  <Typography variant="caption" sx={{ mt: 0.65, display: 'block', color: '#94a3b8' }}>{title}</Typography>
                  <Typography variant="h5" fontWeight={900}>{count == null ? '—' : fmt(count, 0)}</Typography>
                  <Typography variant="caption" color="text.secondary">{help}</Typography>
                </Box>
              ))}
            </Box>
          </Paper>
          {query.data.status === 'empty_dataset' && <Alert severity="info">La cuenta todavía no tiene transacciones evaluables.</Alert>}
          {query.data.status === 'ok' && query.data.items.length === 0 && <Alert severity="info">No se han detectado descensos comparables con los criterios actuales. No significa que el resto de la cartera esté sin cambios.</Alert>}
          <TableContainer component={Paper} elevation={0} sx={{ bgcolor: '#0c172b', border: '1px solid #334155', borderRadius: 2 }}>
            <Table size="small" aria-label="Relaciones con descenso del precio neto unitario" sx={{ '& td, & th': { borderColor: 'rgba(148,163,184,0.13)' } }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 850, color: '#94a3b8' }}>Relación cliente · producto</TableCell>
                  <TableCell sx={{ fontWeight: 850, color: '#94a3b8' }}>Qué ha cambiado</TableCell>
                  <TableCell sx={{ fontWeight: 850, color: '#94a3b8' }}>Evolución reciente</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 850, color: '#94a3b8' }}>Precio neto</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 850, color: '#94a3b8' }}>Vs. referencia</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {query.data.items.map((item) => {
                  const key = `${item.customer_id}|${item.product_id}`;
                  const open = expandedKey === key;
                  return <Fragment key={key}>
                    <TableRow hover role="button" tabIndex={0} aria-expanded={open}
                      onClick={() => setExpandedKey(open ? null : key)}
                      onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); setExpandedKey(open ? null : key); } }}
                      sx={{ cursor: 'pointer', bgcolor: open ? '#1e293b' : 'transparent', '& td': { py: 1.1 }, '&:focus-visible': { outline: '2px solid #38bdf8', outlineOffset: -2 } }}>
                      <TableCell sx={{ minWidth: 260, borderLeft: open ? '3px solid #38bdf8' : '3px solid transparent' }}>
                        <Stack direction="row" gap={1} alignItems="baseline" flexWrap="wrap">
                          <Typography variant="body1" sx={{ fontWeight: 900 }}>{item.customer_label}</Typography>
                          <Typography variant="body2" sx={{ color: '#cbd5e1', fontWeight: 800 }}>{item.product_label}</Typography>
                        </Stack>
                      </TableCell>
                      <TableCell sx={{ minWidth: 160 }}><Typography variant="body2" sx={{ fontWeight: 750 }}>Precio neto inferior a su referencia histórica</Typography></TableCell>
                      <TableCell sx={{ minWidth: 165 }}>
                        <Typography variant="body2" sx={{ color: item.change_vs_previous_pct < 0 ? '#fbbf24' : '#cbd5e1', fontWeight: 800 }}>
                          {item.change_vs_previous_pct < 0 ? 'Precio menor que en el periodo anterior' : item.change_vs_previous_pct > 0 ? 'Precio en recuperación parcial' : 'Sin cambio observado'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">{fmt(item.change_vs_previous_pct)} % respecto al periodo anterior</Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ minWidth: 155 }}>
                        <Typography variant="body2" sx={{ fontWeight: 850, whiteSpace: 'nowrap' }}>{fmt(item.reference_price, 4)} → {fmt(item.current_price, 4)}</Typography>
                        <Typography variant="caption" color="text.secondary">{item.currency_code}/ud.</Typography>
                      </TableCell>
                      <TableCell align="right"><Typography variant="body2" sx={{ color: '#fda4af', fontWeight: 850 }}>{fmt(item.price_delta_pct)} %</Typography></TableCell>
                    </TableRow>
                    {open && <TableRow><TableCell colSpan={5} sx={{ p: 1.1 }}>
                      <Evidence item={item} investigate={() => investigate(item)} />
                    </TableCell></TableRow>}
                  </Fragment>;
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
            <Typography variant="caption" color="text.secondary">Página {history.length + 1} · {fmt(query.data.page.returned_count, 0)} relaciones mostradas</Typography>
            <Stack direction="row" gap={1}>
              <Button size="small" onClick={previousPage} disabled={!history.length}>Anterior</Button>
              <Button size="small" onClick={nextPage} disabled={!query.data.page.has_more}>Siguiente</Button>
            </Stack>
          </Stack>
          <Typography variant="caption" color="text.secondary">Histórico limitado a 1460 días; selección automática del horizonte por relación (90–365 días). No hay memoria de episodios ni revisión comercial automática. Los criterios y el rendimiento aún están pendientes de validación.</Typography>
        </>}
        {selectedCustomer && selectedProduct && <CustomerRelationshipWorkspace
          customerId={selectedCustomer} productId={selectedProduct}
          startDate={params.get('from') || selectedRow?.window_dates.w0_start || null}
          endDate={params.get('to') || query.data?.data_cutoff_date || null}
          comparisonMode={effectiveComparison}
          comparisonStartDate={effectiveComparison === 'custom' ? params.get('comparisonFrom') : null}
          comparisonEndDate={effectiveComparison === 'custom' ? params.get('comparisonTo') : null}
          timeGrain={timeGrain} initialMode="analysis" onClose={closeRelationship}
        />}
      </Stack>
    </Box>
  );
}
